import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, TrendingUp, IndianRupee, CalendarRange, RotateCcw } from "lucide-react";
import { api, API, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Reports() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/reports/summary").then(({ data }) => setData(data)).catch(() => {}); }, []);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/reports/bookings.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "metrix_bookings.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Download failed"); }
  };

  const totals = data?.totals;
  const cards = [
    { label: "Total Collected", value: totals ? fmtINR(totals.total_collected) : "—", icon: IndianRupee },
    { label: "Booking Value", value: totals ? fmtINR(totals.booking_value) : "—", icon: TrendingUp },
    { label: "Total Bookings", value: totals ? totals.bookings : "—", icon: CalendarRange },
    { label: "Refunds", value: totals ? fmtINR(totals.refunds) : "—", icon: RotateCcw },
  ];
  const maxVal = Math.max(1, ...(data?.months || []).map((m) => m.total_collected));

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display text-3xl text-zinc-900">Reports</h1>
        <Button onClick={downloadCsv} data-testid="download-csv-btn" className="gold-gradient text-white font-semibold"><Download size={16} className="mr-2" /> Download Bookings CSV</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-card border border-pink-200 rounded-xl p-5" data-testid={`report-card-${c.label.replace(/\s/g,'-').toLowerCase()}`}>
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-zinc-500">{c.label}</span><c.icon size={18} className="text-gold" /></div>
            <div className="text-2xl font-display text-zinc-900 mt-3">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-card border border-pink-200 rounded-xl p-6">
        <h2 className="font-display text-xl text-zinc-900 mb-5">Monthly Revenue</h2>
        {(data?.months || []).length === 0 ? (
          <p className="text-zinc-500 text-center py-8">No data yet</p>
        ) : (
          <div className="space-y-4">
            {data.months.map((m) => (
              <div key={m.month} data-testid={`report-month-${m.month}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-700">{m.month}</span>
                  <span className="text-gold">{fmtINR(m.total_collected)} <span className="text-zinc-500">· {m.bookings} bookings</span></span>
                </div>
                <div className="h-3 rounded-full bg-pink-100 overflow-hidden">
                  <div className="h-full gold-gradient rounded-full" style={{ width: `${(m.total_collected / maxVal) * 100}%` }} />
                </div>
                <div className="flex gap-4 text-[11px] text-zinc-500 mt-1">
                  <span>Advance {fmtINR(m.advance_collected)}</span>
                  <span>Balance {fmtINR(m.balance_collected)}</span>
                  {m.refunds > 0 && <span className="text-red-400">Refunds {fmtINR(m.refunds)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
